import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

export interface EscalationMatrixEntry {
  id: string;
  tenant_id: string;
  alert_type: string;
  alert_severity: string;
  l1_user_id?: string | null;
  l2_user_id?: string | null;
  l3_user_id?: string | null;
  l1_timer_minutes: number;
  l2_timer_minutes: number;
  l3_timer_minutes: number;
  created_at: string;
  updated_at: string;
}

export interface EscalationMatrixInput {
  alert_type: string;
  alert_severity: string;
  l1_user_id?: string | null;
  l2_user_id?: string | null;
  l3_user_id?: string | null;
  l1_timer_minutes?: number;
  l2_timer_minutes?: number;
  l3_timer_minutes?: number;
}

export interface EscalationMatrixList {
  items: EscalationMatrixEntry[];
  total: number;
}

const escalationKey = ["escalation-matrix"] as const;

export function useEscalationMatrix() {
  return useQuery({
    queryKey: [...escalationKey, "list"],
    queryFn: async () => {
      const { data } = await api.get<EscalationMatrixList>(
        "/api/v1/escalation-matrix",
      );
      return data;
    },
  });
}

export function useCreateEscalationEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: EscalationMatrixInput) => {
      const { data } = await api.post<EscalationMatrixEntry>(
        "/api/v1/escalation-matrix",
        input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: escalationKey }),
  });
}

export function useUpdateEscalationEntry(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<EscalationMatrixInput>) => {
      const { data } = await api.put<EscalationMatrixEntry>(
        `/api/v1/escalation-matrix/${id}`,
        patch,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: escalationKey }),
  });
}

export function useDeleteEscalationEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/escalation-matrix/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: escalationKey }),
  });
}
